import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, off, remove, get, update } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';

const PayableTable = ({ onAddTransaction, onEditTransaction }) => {
  const [transactions, setTransactions] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setTransactions([]);
      return;
    }

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const transactionsRef = ref(database, `users/${encodedEmail}/transactions`);
    
    const transactionsListener = onValue(transactionsRef, (snapshot) => {
      const transactionsData = [];
      snapshot.forEach((childSnapshot) => {
        transactionsData.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      
      // Sort transactions by date (oldest first) and then by voucher number (ascending)
      transactionsData.sort((a, b) => {
        const dateA = new Date(a.voucherDate || a.timestamp);
        const dateB = new Date(b.voucherDate || b.timestamp);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA.getTime() - dateB.getTime(); // Oldest first
        }
        return (parseInt(a.voucherNo) || 0) - (parseInt(b.voucherNo) || 0); // Ascending voucher number
      });
      
      setTransactions(transactionsData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching transactions:', error);
      setLoading(false);
    });

    return () => {
      off(transactionsRef);
    };
  }, [currentUser]);

  const handleDelete = async (transactionId) => {
    if (!currentUser || !transactionId) return;
    
    if (!window.confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) {
      return;
    }
    
    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const transactionRef = ref(database, `users/${encodedEmail}/transactions/${transactionId}`);
      
      // Get transaction data before deleting
      const transactionSnapshot = await get(transactionRef);
      if (!transactionSnapshot.exists()) {
        console.error('Transaction not found');
        alert('Transaction not found.');
        return;
      }
      
      const transactionData = transactionSnapshot.val();
      const paymentAmount = Number(transactionData.paymentAmount) || 0;
      const voucherNo = transactionData.voucherNo || 'Unknown';
      
      // Delete the transaction
      await remove(transactionRef);
      
      // Update the corresponding invoices if they exist
      if (transactionData.transactions && transactionData.transactions.length > 0) {
        const invoiceIds = [...new Set(transactionData.transactions.map(t => t.invoiceId).filter(Boolean))];
        
        for (const invoiceId of invoiceIds) {
          try {
            const invoiceRef = ref(database, `users/${encodedEmail}/invoices/${invoiceId}`);
            const invoiceSnapshot = await get(invoiceRef);
            if (invoiceSnapshot.exists()) {
              const invoiceData = invoiceSnapshot.val();
              const summary = invoiceData.summary || {};
              
              // Calculate new values
              const currentOutstanding = Number(summary.outstanding) || 0;
              const currentTotalPaid = Number(summary.totalPaid) || 0;
              
              const newOutstanding = currentOutstanding + paymentAmount;
              const newTotalPaid = Math.max(0, currentTotalPaid - paymentAmount);
              
              // Determine new payment status
              let newPaymentStatus = 'Pending';
              if (newOutstanding <= 0.01) {
                newPaymentStatus = 'Complete';
              } else if (newTotalPaid > 0) {
                newPaymentStatus = 'Partial';
              }
              
              // Update invoice
              await update(invoiceRef, {
                'summary/outstanding': newOutstanding.toFixed(2),
                'summary/totalPaid': newTotalPaid.toFixed(2),
                'summary/paymentStatus': newPaymentStatus,
                'summary/lastPaymentDate': newTotalPaid > 0 ? summary.lastPaymentDate : null
              });
            }
          } catch (invoiceError) {
            console.error(`Error updating invoice ${invoiceId}:`, invoiceError);
          }
        }
      }
      
      // Show success alert
      alert(`Transaction with Voucher No "${voucherNo}" deleted successfully!`);
      
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert('Error deleting transaction. Please try again.');
    }
  };

  const formatCurrency = (amount) => {
    const num = Number(amount);
    if (isNaN(num)) return '0.00';
    return num.toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Complete':
        return '#10b981';
      case 'Partial':
        return '#f59e0b';
      default:
        return '#ef4444';
    }
  };

  const getPaymentStatusText = (status) => {
    switch (status) {
      case 'Complete':
        return '✅ Complete';
      case 'Partial':
        return '🔄 Partial';
      default:
        return '⏳ Pending';
    }
  };

  // Styles matching PurchaseBillTable
  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontWeight: '600',
      color: '#374151',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    addButton: {
      padding: '0.875rem 2rem',
      borderRadius: '8px',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      transform: 'translateY(0)',
    },
    tableContainer: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
    },
    tableHeader: {
      padding: '1rem 0.75rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
    },
    tableCell: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
    },
    noData: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#718096',
      fontSize: '1.1rem',
    },
    srNoCell: {
      fontWeight: '600',
      textAlign: 'center',
      width: '60px',
    },
    actionCell: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0.5rem',
    },
    editButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    paymentStatusBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '600',
      color: 'white',
      textAlign: 'center',
      display: 'inline-block',
      minWidth: '80px',
    },
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={styles.noData}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>🔐</div>
          <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>Payment Transactions</h3>
          <p>Please log in to view your payment transactions.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Payment Transactions</h1>
        <button
          onClick={onAddTransaction}
          style={styles.addButton}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
          }}
        >
          + Add Transaction
        </button>
      </div>

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.noData}>
            <p>⏳ Loading transactions...</p>
          </div>
        ) : transactions.length > 0 ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.tableHeader, ...styles.srNoCell }}>Sr.No</th>
                <th style={styles.tableHeader}>Voucher No</th>
                <th style={styles.tableHeader}>Date</th>
                <th style={styles.tableHeader}>Supplier</th>
                <th style={styles.tableHeader}>Payment By</th>
                <th style={styles.tableHeader}>Original Amount</th>
                <th style={styles.tableHeader}>Payment Amount</th>
                <th style={styles.tableHeader}>Outstanding</th>
                <th style={styles.tableHeader}>Status</th>
                <th style={styles.tableHeader}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction, index) => {
                const originalAmount = Number(transaction.originalAmount || transaction.totalAmount || 0);
                const paymentAmount = Number(transaction.paymentAmount || 0);
                const outstandingAmount = Number(transaction.netAmount || 0);
                
                // Determine payment status based on outstanding amount
                let paymentStatus = 'Pending';
                if (outstandingAmount <= 0.01) {
                  paymentStatus = 'Complete';
                } else if (paymentAmount > 0 && outstandingAmount < originalAmount) {
                  paymentStatus = 'Partial';
                }

                return (
                  <tr 
                    key={transaction.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <td style={{ ...styles.tableCell, ...styles.srNoCell }}>{index + 1}</td>
                    <td style={styles.tableCell}>
                      <span style={{ fontWeight: '600' }}>{transaction.voucherNo || '-'}</span>
                    </td>
                    <td style={styles.tableCell}>{formatDate(transaction.voucherDate)}</td>
                    <td style={styles.tableCell}>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                          {transaction.supplier || '-'}
                        </div>
                        {transaction.supplierAddress && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {transaction.supplierAddress}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', 
                        backgroundColor: '#f3f4f6', 
                        borderRadius: '4px',
                        fontSize: '0.8rem'
                      }}>
                        {transaction.paymentBy || '-'}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{ fontWeight: '600' }}>₹{formatCurrency(originalAmount)}</span>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{ fontWeight: '600', color: '#10b981' }}>₹{formatCurrency(paymentAmount)}</span>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{ 
                        fontWeight: '600', 
                        color: outstandingAmount > 0 ? '#ef4444' : '#10b981' 
                      }}>
                        ₹{formatCurrency(outstandingAmount)}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <span 
                        style={{
                          ...styles.paymentStatusBadge,
                          backgroundColor: getPaymentStatusColor(paymentStatus)
                        }}
                      >
                        {getPaymentStatusText(paymentStatus)}
                      </span>
                    </td>
                    <td style={{ ...styles.tableCell, ...styles.actionCell }}>
                      <button 
                        style={styles.editButton}
                        onClick={() => onEditTransaction && onEditTransaction(transaction)}
                        title="Edit Transaction"
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                        }}
                      >
                        ✏️ 
                      </button>
                   
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={styles.noData}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>💳</div>
            <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>No Payment Transactions Found</h3>
            <p style={{ marginBottom: '1.5rem' }}>Get started by adding your first payment transaction!</p>
            <button 
              onClick={onAddTransaction}
              style={{
                ...styles.addButton,
                fontSize: '1rem',
                padding: '1rem 2rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
              }}
            >
              + Add Your First Transaction
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayableTable;